import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { Field, Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { getAssetPrice, postOrder } from '../../Redux/Actions/transactionService';

function OrderDetailsModal({ open, handleClose, order, showBanner }) {
  const dispatch = useDispatch();
  const [formValues, setFormValues] = useState({});
  const [assetPrices, setAssetPrices] = useState(null);

  useEffect(() => {
    if (order) {
      const custodian = order.strategy?.custodian || 'COINBASE';
      const symbols = order.strategy?.breakups?.map(asset => asset.asset.symbol) || [];

      const payload = {
        map: {
          [custodian]: symbols
        }
      };

      dispatch(getAssetPrice(payload)).then(response => {
        if (response.payload.statusCode === 201) {
          setAssetPrices(response.payload?.data?.[custodian]);
        }
      });
    }
  }, [order, dispatch]);

  const validationSchema = Yup.object().shape({
    orderType: Yup.string().required('Order type is required'),
    limitPrices: Yup.object().when('orderType', {
      is: (val) => val === 'LMT' || val === 'TWAP',
      then: (schema) =>
        schema.test(
          'limitPricesFilled',
          'All limit prices are required and must be numbers.',
          (limitPrices) =>
            limitPrices &&
            Object.values(limitPrices).every(
              (value) => value !== undefined && value !== '' && !isNaN(value)
            )
        ),
    }),
    twapStartDateTime: Yup.string().when('orderType', {
      is: 'TWAP',
      then: (schema) => schema.required('Start date and time are required'),
    }),
    twapEndDateTime: Yup.string().when('orderType', {
      is: 'TWAP',
      then: (schema) =>
        schema
          .required('End date and time are required')
          .test(
            'endDateTimeValid',
            'End date and time must be greater than start date and time.',
            function (value) {
              const { twapStartDateTime } = this.parent;
              return !value || !twapStartDateTime || new Date(value) > new Date(twapStartDateTime);
            }
          ),
    }),
  });

  const handleSubmit = (values) => {
    setFormValues(values);

    const orderId = order?.id;
    const requestBody = {
      type: values.orderType,
      orderItems: []
    };

    if (values.orderType === 'LMT' || values.orderType === 'TWAP') {
      requestBody.orderItems = order.strategy?.breakups?.map((asset) => ({
        symbol: asset.asset.symbol,
        price: values.limitPrices[asset.id],
      }));
    }

    if (values.orderType === 'TWAP') {
      requestBody.startTime = values.twapStartDateTime;
      requestBody.endTime = values.twapEndDateTime;
    }

    dispatch(postOrder({ requestBody, orderId })).then(response => {
      if (response.statusCode === 201) {
        showBanner("Order placed successfully!", "success");
        handleClose();
      } else {
        showBanner("Failed to place order.", "error");
      }
    });
  };

  if (!order) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{ '& .MuiDialog-paper': { width: '520px' } }}
    >
      <DialogTitle>Order Details</DialogTitle>
      <Formik
        initialValues={{
          orderType: '',
          twapStartDateTime: '',
          twapEndDateTime: '',
          limitPrices: {},
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, setFieldValue, isValid }) => (
          <Form>
            <DialogContent dividers>
              <Typography>Amount: {order.amount.toFixed(2)}</Typography>
              <Field
                as={TextField}
                select
                label="Select Order Type"
                name="orderType"
                value={values.orderType}
                onChange={(e) => {
                  handleChange(e);
                  setFieldValue('limitPrices', {});
                  setFieldValue('twapStartDateTime', '');
                  setFieldValue('twapEndDateTime', '');
                }}
                fullWidth
                margin="normal"
              >
                <MenuItem value="MKT">Market Order</MenuItem>
                <MenuItem value="LMT">Limit Order</MenuItem>
                <MenuItem value="TWAP">TWAP Order</MenuItem>
              </Field>

              {values.orderType === 'LMT' &&
                order.strategy?.breakups?.map((asset) => (
                  <Box key={asset.id} display="flex" alignItems="center">
                    <Field
                      as={TextField}
                      label={`Limit Price for ${asset.asset.name}`}
                      type="number"
                      name={`limitPrices.${asset.id}`}
                      value={values.limitPrices[asset.id] || ''}
                      onChange={handleChange}
                      fullWidth
                      margin="normal"
                      required
                    />
                    <Typography marginLeft={2}>
                      Market Price: ${assetPrices?.[asset.asset.symbol] || 'N/A'}
                    </Typography>
                    <ErrorMessage
                      name={`limitPrices.${asset.id}`}
                      render={(msg) => (
                        <FormHelperText error>{msg}</FormHelperText>
                      )}
                    />
                  </Box>
                ))}

              {values.orderType === 'TWAP' && (
                <>
                  <Field
                    as={TextField}
                    label="Start Date and Time"
                    type="datetime-local"
                    name="twapStartDateTime"
                    value={values.twapStartDateTime}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                  />
                  <ErrorMessage
                    name="twapStartDateTime"
                    render={(msg) => (
                      <FormHelperText error>{msg}</FormHelperText>
                    )}
                  />

                  <Field
                    as={TextField}
                    label="End Date and Time"
                    type="datetime-local"
                    name="twapEndDateTime"
                    value={values.twapEndDateTime}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                  />
                  <ErrorMessage
                    name="twapEndDateTime"
                    render={(msg) => (
                      <FormHelperText error>{msg}</FormHelperText>
                    )}
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="secondary">
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={!isValid}>
                Submit
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

export default OrderDetailsModal;
